import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { PATHS } from '@/routes/paths';
import { Loader } from '@/components/custom/loader';
import StepIdentification from './StepIdentification';
import StepDeadline from './StepDeadline';
import StepReview from './StepReview';

const validationSchema = Yup.object().shape({
  name: Yup.string().min(3, 'Mínimo 3 caracteres').required('Obrigatório'),
  description: Yup.string(),
  starts_at: Yup.date().required('Obrigatório').typeError('Data inválida'),
  ends_at: Yup.date().min(Yup.ref('starts_at'), 'Fim deve ser após o início').required('Obrigatório').typeError('Data inválida'),
  regional_id: Yup.number().nullable()
});

const empty = { name: '', description: '', starts_at: '', ends_at: '', regional_id: '' };

export default function SelectionProcessContent({ id, currentStep, setCurrentStep, initialValues, loadingInitial }) {
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const formik = useFormik({
    initialValues: initialValues || empty,
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => {}
  });

  useEffect(() => {
    if (initialValues) {
      formik.setValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const buildPayload = () => ({
    ...formik.values,
    regional_id: formik.values.regional_id || null
  });

  const handleSave = async ({ publish = false } = {}) => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({ name: true, description: true, starts_at: true, ends_at: true });
      toast.error('Corrija os erros antes de salvar.');
      return;
    }

    formik.setSubmitting(true);
    try {
      const payload = buildPayload();
      let savedId;

      if (isEditing) {
        await selectionProcessesAdmin.update(id, payload);
        savedId = id;
        toast.success('Seletivo atualizado com sucesso!');
      } else {
        const res = await selectionProcessesAdmin.create(payload);
        savedId = res.data.id;
        if (publish) {
          await selectionProcessesAdmin.publish(savedId);
          toast.success('Seletivo criado e publicado!');
        } else {
          toast.success('Seletivo salvo como rascunho!');
        }
      }

      navigate(PATHS.ADMIN_SELETIVO.DETAIL(savedId));
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.response?.data?.message || err?.message || 'Erro ao salvar seletivo.';
      toast.error(message);
    } finally {
      formik.setSubmitting(false);
    }
  };

  if (loadingInitial) return <Loader />;

  if (currentStep === 1) {
    return (
      <StepIdentification
        formik={formik}
        onNext={() => setCurrentStep(2)}
      />
    );
  }

  if (currentStep === 2) {
    return (
      <StepDeadline
        formik={formik}
        onBack={() => setCurrentStep(1)}
        onNext={() => setCurrentStep(3)}
      />
    );
  }

  return (
    <StepReview
      formik={formik}
      isEditing={isEditing}
      submitting={formik.isSubmitting}
      onBack={() => setCurrentStep(2)}
      onSaveDraft={() => handleSave({ publish: false })}
      onPublish={() => handleSave({ publish: true })}
    />
  );
}
